/*
 * cart-notification-personalisation.js
 * -------------------------------------------------------------------------
 * Personalisation capture + checkout gating for the ADD-TO-CART POPUP
 * (snippets/cart-notification.liquid).
 *
 * WHY THIS EXISTS
 * The popup carries its own "Check out" button (#cart-notification-form
 * button[name="checkout"]) which posts straight to checkout — it never passes
 * through the cart page. So the cart page's gate (cart-personalisation.js) is
 * simply not in the path, and a shopper who adds from a collection card can
 * check out with no "Name to be printed" at all. The fields themselves are
 * rendered into the popup by sections/cart-notification-product.liquid via the
 * shared personalisation-fields.liquid snippet, so the labels, max lengths and
 * required flags come from King's own config — not a hardcoded list.
 *
 * WHY IT IS A SEPARATE FILE, SCOPED TO THE POPUP
 * cart-personalisation.js and pdp-personalisation.js both gate document-wide
 * and share the same data-cp-gated attribute. A third document-wide script
 * would fight them: the popup's opinion of ".magic-buy-btn" would un-gate a
 * button the PDP script had deliberately gated (and vice versa), with the last
 * MutationObserver to fire winning. Everything here is therefore scoped inside
 * #cart-notification and never reaches an element outside it.
 *
 * 1.3.1 — THE BUTTON IS NO LONGER DISABLED
 * Up to 1.3.0 the popup's Check out was greyed out and disabled while the name
 * was empty; shoppers read that as broken. Now it stays live: a click with a
 * missing name is intercepted in the capture phase, the field turns red, a
 * "Please enter name to be printed" popup appears (window.ZNKing.notify from
 * king-personalisation.js) and the cursor lands in the first empty field.
 * syncGates() only RELEASES anything an older cached copy may have gated.
 *
 * SCOPE LIMIT (deliberate, documented)
 * This gates on the fields VISIBLE IN THE POPUP — i.e. the line just added,
 * which is the only line the shopper can fix from here. If a DIFFERENT cart
 * line is missing its name, the cart page still catches it; the popup does not,
 * because the popup markup only ever contains the added line.
 * -------------------------------------------------------------------------
 */
(function () {
  var ROOT_SEL = '#cart-notification';

  // The popup's own checkout affordances. "View cart" is deliberately NOT
  // gated: it is the escape hatch to the full cart, where every line can be
  // fixed.
  var CHECKOUT_SEL = '#cart-notification-form button[name="checkout"], #cart-notification-form button[type="submit"]';

  // Razorpay's real triggers, in case Magic Checkout ever injects into the
  // popup as it does elsewhere. Note .magic-buy-btn does NOT contain the string
  // "magic-checkout", so a [class*="magic-checkout"] selector misses it.
  var RZP_SEL = '.magic-buy-btn, .magic-buy-btn__parent';

  // NEVER gate or intercept these: they are the Razorpay PORTAL itself (modal,
  // backdrop, iframe) plus its config/script containers. Gating them would dim
  // an open checkout and strand the shopper mid-payment.
  var PORTAL_SEL = [
    '.razorpay-container',
    '.razorpay-backdrop',
    '.razorpay-checkout-frame',
    '#magic-checkout-data-container',
    '#magic-merchant-js'
  ].join(',');

  function root() {
    return document.querySelector(ROOT_SEL);
  }

  // Every query in this file goes through here, so nothing outside the popup
  // can ever be touched.
  function within(sel) {
    var r = root();
    if (!r) return [];
    return Array.prototype.slice.call(r.querySelectorAll(sel));
  }

  function cpInputs() {
    return within('[data-cp-input]');
  }

  function markError(inp, on) {
    var wrap = inp.closest('[data-cp]');
    if (!wrap) return;
    var err = wrap.querySelector('[data-cp-error]');
    wrap.classList.toggle('cart-personalise--invalid', !!on);
    // Amber "Required" card while a required field is empty, plain once typed.
    var required = inp.getAttribute('data-cp-required') === 'true';
    wrap.classList.toggle('cart-personalise--empty', required && !inp.value.trim() && !on);
    if (err) err.hidden = !on;
  }

  var MISSING_MSG = 'Please enter name to be printed';

  // Shows the popup and drops the cursor into the first empty field. Falls
  // back to a plain alert if king-personalisation.js has not loaded.
  function complain(miss) {
    if (window.ZNKing && typeof window.ZNKing.notify === 'function') {
      window.ZNKing.notify(MISSING_MSG, miss);
    } else {
      miss.scrollIntoView({ behavior: 'smooth', block: 'center' });
      miss.focus();
      try { window.alert(MISSING_MSG); } catch (e) {}
    }
  }

  // Returns the first required-but-empty input (and flags all of them).
  function firstMissing() {
    var miss = null;
    cpInputs().forEach(function (inp) {
      var req = inp.getAttribute('data-cp-required') === 'true';
      var empty = !inp.value.trim();
      if (req && empty) { markError(inp, true); if (!miss) miss = inp; }
      else { markError(inp, false); }
    });
    return miss;
  }

  // Shopify derives a cart line's key from variant + properties, so writing a
  // property RE-KEYS the line: 51454…:11b74055… becomes 51454…:1befac33…. The
  // key sitting in the DOM is stale the moment the first save lands, and the
  // next save would address a line that no longer exists — silently losing the
  // shopper's correction while the field still looks filled. After every write
  // we find the re-keyed line and re-point the DOM at it.
  function sameProps(a, b) {
    a = a || {}; b = b || {};
    var ka = Object.keys(a), kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every(function (k) { return String(a[k]) === String(b[k]); });
  }

  function rekey(oldKey, cart, variantId, props) {
    if (!cart || !cart.items) return;
    var fresh = cart.items.filter(function (it) {
      return String(it.variant_id || it.id) === String(variantId) && sameProps(it.properties, props);
    })[0];
    if (!fresh || fresh.key === oldKey) return;
    Array.prototype.slice.call(
      document.querySelectorAll('[data-line-key="' + oldKey + '"]')
    ).forEach(function (el) { el.setAttribute('data-line-key', fresh.key); });
  }

  // Persist one field to its cart line, merging existing line-item properties
  // so we never clobber other King options. Serialised through `saving` so
  // rapid blurs don't race on /cart/change.js.
  var saving = Promise.resolve();
  function saveField(inp) {
    var label = inp.getAttribute('data-cp-label');
    var val = inp.value.trim();
    saving = saving
      .then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Read the key HERE, not before the fetch: an earlier queued save may
        // have re-keyed the line while this one was waiting its turn.
        var key = inp.getAttribute('data-line-key');
        var line = (cart.items || []).filter(function (it) { return it.key === key; })[0];
        if (!line) return null;                 // line gone (removed) — nothing to write
        var props = {};
        if (line.properties) {
          Object.keys(line.properties).forEach(function (k) { props[k] = line.properties[k]; });
        }
        // Clearing the box must CLEAR the stored value, not leave the old one
        // behind. `properties: {}` is a no-op to Shopify and deleting the key
        // does nothing, so write an empty string — the only thing that actually
        // overwrites. Otherwise a shopper who erases a name still checks out
        // with the previous one attached to the line.
        props[label] = val;
        // /cart/change.js resets the line to quantity 1 unless quantity is sent
        // explicitly. Preserve the shopper's chosen quantity.
        var qty = typeof line.quantity === 'number' ? line.quantity : 1;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty, properties: props })
        })
          .then(function (r) { return r.json(); })
          .then(function (updated) { rekey(key, updated, line.variant_id || line.id, props); });
      })
      .catch(function () { /* keep the queue alive on transient errors */ });
    return saving;
  }

  // ---- gating (release-only since 1.3.1) ----------------------------------
  // Check out is never disabled any more; gateEl(el, false) is kept only to
  // restore a control an older cached copy of this script may have gated.
  var GATE_ATTR = 'data-cp-gated';

  function gateEl(el, off) {
    if (!el) return;
    if (off) {
      if (!el.hasAttribute(GATE_ATTR)) {
        el.setAttribute(GATE_ATTR, '1');
        el.dataset.cpPrevPe = el.style.pointerEvents || '';
        el.dataset.cpPrevOp = el.style.opacity || '';
      }
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.4';
      el.setAttribute('aria-disabled', 'true');
      if ('disabled' in el) { try { el.disabled = true; } catch (e) {} }
    } else if (el.hasAttribute(GATE_ATTR)) {
      el.style.pointerEvents = el.dataset.cpPrevPe || '';
      el.style.opacity = el.dataset.cpPrevOp || '';
      el.setAttribute('aria-disabled', 'false');
      if ('disabled' in el) { try { el.disabled = false; } catch (e) {} }
      el.removeAttribute(GATE_ATTR);
      delete el.dataset.cpPrevPe;
      delete el.dataset.cpPrevOp;
    }
  }

  function syncGates() {
    // Release only. The click/submit guards below are the whole defence now.
    within(CHECKOUT_SEL + ',' + RZP_SEL).forEach(function (el) {
      if (el.matches(PORTAL_SEL) || el.closest(PORTAL_SEL)) return;   // never the portal
      gateEl(el, false);
    });
    // Keep the amber/red field styling in step with the current values.
    cpInputs().forEach(function (inp) {
      var wrap = inp.closest('[data-cp]');
      if (!wrap || wrap.classList.contains('cart-personalise--invalid')) return;
      var required = inp.getAttribute('data-cp-required') === 'true';
      wrap.classList.toggle('cart-personalise--empty', required && !inp.value.trim());
    });
  }

  var gateTimer = null;
  function scheduleSync() {
    if (gateTimer) return;
    gateTimer = setTimeout(function () { gateTimer = null; syncGates(); }, 120);
  }

  // ---- events -------------------------------------------------------------
  // The popup's innerHTML is replaced by CartNotification.renderContents() on
  // every add-to-cart, so everything is delegated off document rather than
  // bound to the elements themselves.
  function inPopup(el) {
    return !!(el && el.closest && el.closest(ROOT_SEL));
  }

  document.addEventListener('input', function (e) {
    if (!e.target.matches || !e.target.matches('[data-cp-input]')) return;
    if (!inPopup(e.target)) return;
    markError(e.target, false);
    syncGates();
  });

  // blur doesn't bubble → capture phase.
  document.addEventListener('blur', function (e) {
    if (!e.target.matches || !e.target.matches('[data-cp-input]')) return;
    if (!inPopup(e.target)) return;
    saveField(e.target).then(syncGates);
  }, true);

  // THE defence: with Check out live, this is what stops a nameless checkout.
  // WINDOW capture, not document capture. Razorpay's magic-shopify.js binds
  // its own click handler on document in the CAPTURE phase and loads before
  // this file, so a document-capture listener here runs AFTER theirs and the
  // portal is already opening by the time we call preventDefault (measured on
  // 1.3.1: document-capture stopImmediatePropagation still opened the portal;
  // window-capture did not). window capture is the only phase that runs first.
  window.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t || !inPopup(t)) return;
    if (t.closest(PORTAL_SEL)) return;              // an open portal — hands off
    var btn = t.closest(CHECKOUT_SEL) || t.closest(RZP_SEL);
    if (!btn || !inPopup(btn)) return;
    if (!cpInputs().length) return;                 // nothing to personalise here

    // Re-dispatched click after a successful save — let it through so the
    // native submit (or Razorpay's handler) runs normally.
    if (btn.getAttribute('data-cp-passed') === '1') {
      btn.removeAttribute('data-cp-passed');
      return;
    }

    var miss = firstMissing();
    if (miss) {
      e.preventDefault();
      e.stopPropagation();
      complain(miss);
      return;
    }

    // Valid — flush every pending save BEFORE letting checkout proceed, so the
    // name is on the line item by the time the order is created. Without this
    // an in-flight blur-save can lose the race with the navigation.
    e.preventDefault();
    e.stopPropagation();
    btn.classList.add('loading');
    btn.setAttribute('aria-busy', 'true');
    Promise.all(cpInputs().map(saveField)).then(function () { return saving; }).then(function () {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-busy');
      btn.setAttribute('data-cp-passed', '1');
      btn.click();
    });
  }, true);

  // Enter-key / non-click submits of the popup form.
  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('#cart-notification-form')) return;
    if (!cpInputs().length) return;
    var miss = firstMissing();
    if (miss) {
      e.preventDefault();
      e.stopPropagation();
      complain(miss);
    }
  }, true);

  function init() {
    syncGates();
    // ALWAYS attach the observer, even with no fields present right now: the
    // popup starts empty and is filled in only when an item is added. Bailing
    // early here would mean the gate never runs for the rest of the session.
    // (That exact bug bit pdp-personalisation.js against King's async render.)
    try {
      new MutationObserver(scheduleSync)
        .observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) { /* observer unsupported — click guard still applies */ }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
